import React, { useMemo } from "react";

import {
  createComparisonFlamebearer,
  createFormatterForProfileType,
  FlamebearerCall,
  FlamebearerNode,
  getProfileTypeItems,
  getProfileTypeTitle
} from "@platformsh/observability-graph/ContinuousProfiling";
import { useIntl } from "react-intl";

import Dialog from "ds/Dialog";
import useConprofContext from "src/organization/pages/project/environment/observabilitySandbox/experiments/Conprof/hooks/useConprofContext";
import {
  ComparisonSide,
  Range
} from "src/organization/pages/project/environment/observabilitySandbox/experiments/Conprof/types";

import ComparisonFlamebearerGraphNode from "./ComparisonFlamebearerGraphNode";
import FlamebearerGraphSkeleton from "./FlamebearerGraphSkeleton";
import * as S from "./styles";

type Props = {
  highlightedCall?: FlamebearerCall;
  onCallHighlighted: (call: FlamebearerCall | undefined) => void;
  side: ComparisonSide;
};

const ComparisonFlamebearerGraph = ({
  highlightedCall,
  onCallHighlighted,
  side
}: Props) => {
  const intl = useIntl();
  const { data } = useConprofContext() ?? {};
  const profileType = data?.profileType;
  const flamebearerData =
    side === "A" ? data?.flamebearerSideAData : data?.flamebearerSideBData;
  const otherSideFlamebearerData =
    side === "A" ? data?.flamebearerSideBData : data?.flamebearerSideAData;

  const comparisonFlamebearer = useMemo(() => {
    if (!flamebearerData) {
      return undefined;
    }

    return createComparisonFlamebearer(
      flamebearerData.flamebearer,
      otherSideFlamebearerData?.flamebearer
    );
  }, [flamebearerData, otherSideFlamebearerData]);

  const { numberOfLines, nodes } = useMemo((): {
    numberOfLines: number;
    nodes: FlamebearerNode[];
  } => {
    if (!comparisonFlamebearer) {
      return {
        numberOfLines: 0,
        nodes: []
      };
    }

    const traverse = (
      node: FlamebearerNode
    ): {
      numberOfLines: number;
      nodes: FlamebearerNode[];
    } => {
      let numberOfLines: number = node.level;
      const nodes: FlamebearerNode[] = [node];

      for (const child of node.children) {
        const { numberOfLines: childDepth, nodes: childNodes } =
          traverse(child);
        nodes.push(...childNodes);
        if (childDepth > numberOfLines) {
          numberOfLines = childDepth;
        }
      }

      return { numberOfLines, nodes };
    };

    return traverse(comparisonFlamebearer.rootNode);
  }, [comparisonFlamebearer]);

  const formatter = useMemo(
    () => (profileType ? createFormatterForProfileType(profileType) : null),
    [profileType]
  );

  const topNode = comparisonFlamebearer?.rootNode;
  const range: Range = useMemo(
    () =>
      topNode ? { from: topNode.start, to: topNode.end } : { from: 0, to: 0 },
    [topNode]
  );

  if (!profileType) {
    return null;
  }
  if (!comparisonFlamebearer || !formatter) {
    return <FlamebearerGraphSkeleton />;
  }

  const diff = formatter.toCondensedString(
    Math.abs(comparisonFlamebearer.deltaPeak ?? 0)
  );

  return (
    <>
      <S.Header>
        <S.HeaderBullet side={side}>{side}</S.HeaderBullet>
        <S.HeaderLegend>
          {side === "B" && (
            <Dialog
              align="center"
              size="large"
              trigger={
                <S.HeaderLegendScale>
                  <S.HeaderLegendScaleText>
                    {intl.formatMessage(
                      {
                        id: "conprof.flamebearer.comparison.legend.less"
                      },
                      {
                        item: getProfileTypeItems(profileType.name)
                      }
                    )}
                  </S.HeaderLegendScaleText>
                  <S.HeaderLegendScaleColors className="less">
                    <S.HeaderLegendScaleColorMain>{`-${diff}`}</S.HeaderLegendScaleColorMain>
                    <S.HeaderLegendScaleColor className="first" />
                    <S.HeaderLegendScaleColor className="second" />
                  </S.HeaderLegendScaleColors>
                  <S.HeaderLegendScaleColors className="more">
                    <S.HeaderLegendScaleColor className="second" />
                    <S.HeaderLegendScaleColor className="first" />
                    <S.HeaderLegendScaleColorMain>{`+${diff}`}</S.HeaderLegendScaleColorMain>
                  </S.HeaderLegendScaleColors>
                  <S.HeaderLegendScaleText>
                    {intl.formatMessage(
                      {
                        id: "conprof.flamebearer.comparison.legend.more"
                      },
                      {
                        item: getProfileTypeItems(profileType.name)
                      }
                    )}
                  </S.HeaderLegendScaleText>
                </S.HeaderLegendScale>
              }
              title={intl.formatMessage({
                id: "conprof.flamebearer.comparison.tooltip.title"
              })}
              link={
                <S.HeaderLegendTooltip>
                  <S.HeaderLegendTooltipSection>
                    <S.HeaderLegendTooltipSectionBullet className="less" />
                    <div>
                      {intl.formatMessage(
                        {
                          id: "conprof.flamebearer.comparison.tooltip.less"
                        },
                        {
                          profileTypeTitle: getProfileTypeTitle(
                            profileType.name
                          )
                        }
                      )}
                    </div>
                  </S.HeaderLegendTooltipSection>
                  <S.HeaderLegendTooltipSection>
                    <S.HeaderLegendTooltipSectionBullet className="more" />
                    <div>
                      {intl.formatMessage(
                        {
                          id: "conprof.flamebearer.comparison.tooltip.more"
                        },
                        {
                          profileTypeTitle: getProfileTypeTitle(
                            profileType.name
                          )
                        }
                      )}
                    </div>
                  </S.HeaderLegendTooltipSection>
                </S.HeaderLegendTooltip>
              }
            />
          )}
          <S.HeaderLegendOnly side={side} />
          <div>
            {intl.formatMessage(
              {
                id: "conprof.flamebearer.comparison.legend.only"
              },
              {
                side
              }
            )}
          </div>
        </S.HeaderLegend>
      </S.Header>
      <S.Container numberOfLines={numberOfLines}>
        {nodes.map((node: FlamebearerNode) => (
          <ComparisonFlamebearerGraphNode
            key={`${node.callstack.join(":")}:${node.call.id}`}
            highlighted={
              !!(highlightedCall && highlightedCall.raw === node.call.raw)
            }
            onCallHighlighted={onCallHighlighted}
            node={node}
            flamebearer={comparisonFlamebearer}
            profileType={profileType}
            range={range}
            side={side}
          />
        ))}
      </S.Container>
    </>
  );
};

export default ComparisonFlamebearerGraph;
